// src/components/StaffSideBar.jsx
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaUserEdit,
  FaStore,
  FaUsers,
  FaMoneyBill,
  FaCommentDots,
  FaSignOutAlt,
  FaGlobe,
  FaUtensils,
  FaFileInvoice,
  FaBoxOpen,
  FaBars,
  FaBell,
  FaEnvelope,
  FaTimes,
  FaChevronDown,
  FaChevronRight,
  FaListAlt,
  FaChair,
  FaUser,
  FaChartPie,
  FaCashRegister,
  FaQrcode,
  FaUserCircle,
  FaTruck,
  FaClipboardList
} from "react-icons/fa";
import api from "../../utils/api";
import {
  getUserdata,
  logoutUser
} from "../../helper/generalHelper";
import logo from "../../../src/assets/logiscreen.png";
import LogoIcon from "../../assets/Logo";

const StaffSideBar = ({ isOpen, onClose }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  // Get user permissions array
  let userPermissions = getUserdata("permissions") || [];
  if (typeof userPermissions === "string") {
    try { userPermissions = JSON.parse(userPermissions); } catch(e) { userPermissions = []; }
  }
  const hasPerm = (ids) => {
    if (!ids || ids.length === 0) return true; // If no IDs required, it's accessible (or handled separately)
    return Array.isArray(userPermissions) && userPermissions.some(p => ids.includes(Number(p)));
  };

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const businessItems = [
    { label: "Business", icon: <FaStore />, path: "/restaurant-staff/employee/business", perms: [131] }, // Using Business View perm (131)
  ];

  const managementItems = [
    {
      label: "Menus",
      icon: <FaListAlt />,
      path: "/restaurant-staff/employee/menu",
      perms: [171, 175, 177, 181, 183, 185], // Removed 173 (Menu View) and 179 (Category View) so POS staff don't see it
      subMenu: [
        { label: "Add Menu", path: "/restaurant-staff/employee/menu", perms: [171] },
        { label: "All Menus", path: "/restaurant-staff/employee/menu/items", perms: [171, 175, 177] }, // Removed 173
        { label: "Category", path: "/restaurant-staff/employee/menu/categories", perms: [181, 183, 185] }, // Removed 179
      ],
    },
    { label: "Tables", icon: <FaChair />, path: "/restaurant-staff/employee/table", perms: [7, 8, 9] }, // Removed 6 (Table View)
    { label: "Inventory", icon: <FaBoxOpen />, path: "/restaurant-staff/employee/inventory", perms: [10, 11, 12, 13] },
    { label: "Customer", icon: <FaUsers />, path: "/restaurant-staff/employee/customers", perms: [133, 135, 137, 139] },
    { label: "Vendor", icon: <FaUsers />, path: "/restaurant-staff/employee/vendors", perms: [141, 143, 145, 147] },
    { label: "Staff", icon: <FaUserEdit />, path: "/restaurant-staff/employee/staff", perms: [14, 15, 16, 17] },
    { label: "Expense / Income", icon: <FaMoneyBill />, path: "/restaurant-staff/employee/expenseincome", perms: [18, 19, 20, 21] },
  ];

  const helpItems = [
    { label: "Help Chat", icon: <FaCommentDots />, path: "/restaurant-staff/employee/help", perms: [149] },
  ];

  const appsItems = [
    { label: "Counter (POS)", icon: <FaCashRegister />, path: "/restaurant-staff/employee/counter", perms: [123] },
    { label: "KOT - Display", icon: <FaUtensils />, path: "/restaurant-staff/employee/kot", perms: [22, 23, 24, 25] },
    { label: "Delivery / Dispatch", icon: <FaTruck />, path: "/restaurant-staff/employee/dispatch", perms: [187, 188, 189, 190] },
    { label: "Orders", icon: <FaClipboardList />, path: "/restaurant-staff/employee/orders", perms: [26, 27, 28, 29] },
    { label: "Receipt", icon: <FaFileInvoice />, path: "/restaurant-staff/employee/receipt", perms: [153, 155] },
  ];

  const settingsItems = [
    { label: "Language - English", icon: <FaGlobe />, path: "/restaurant-staff/employee/language", perms: [157] },
  ];

  // Render menu item with permission check
  const renderMenuItem = (item, index) => {
    // Check if the item itself has permission
    if (item.perms && item.perms.length > 0 && !hasPerm(item.perms)) return null;

    if (item.subMenu) {
      // Filter submenus by permission
      const visibleSubMenu = item.subMenu.filter(sub => !sub.perms || sub.perms.length === 0 || hasPerm(sub.perms));
      if (visibleSubMenu.length === 0) return null;

      return (
        <div key={index}>
          <button
            onClick={() => toggleDropdown(index)}
            className={`w-full flex items-center justify-between text-sm p-2 rounded transition-all ${
              location.pathname.startsWith(item.path)
                ? "bg-rose-600 text-white"
                : "text-gray-700 hover:bg-gray-100"
            }`}
          >
            <div className="flex items-center gap-2">
              {item.icon}
              <span>{item.label}</span>
            </div>
            {openDropdown === index ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
          </button>

          {openDropdown === index && (
            <div className="ml-6 mt-1 space-y-1">
              {visibleSubMenu.map((sub, j) => (
                <Link
                  key={j}
                  to={sub.path}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (onClose) onClose();
                  }}
                  className={`block text-sm p-2 rounded transition-all ${
                    location.pathname === sub.path
                      ? "bg-rose-500 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  {sub.label}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={index}
        to={item.path}
        className={`w-full flex items-center justify-between text-sm p-2 rounded transition-all ${
          location.pathname === item.path || (item.path === "/restaurant-staff/employee/counter" && (location.pathname.startsWith("/restaurant-staff/employee/create-order") || location.pathname.startsWith("/restaurant-staff/employee/table-selection")))
            ? "bg-rose-600 text-white"
            : "text-gray-700 hover:bg-gray-100"
        }`}
        onClick={() => {
          setMobileMenuOpen(false);
          if (onClose) onClose();
        }}
      >
        <div className="flex items-center gap-2">
          {item.icon}
          <span>{item.label}</span>
        </div>
      </Link>
    );
  };

  const renderSection = (title, items) => {
    // Check if any items in this section are visible
    const visibleItems = items.filter(item => {
      if (item.perms && item.perms.length > 0 && !hasPerm(item.perms)) return false;
      if (item.subMenu) {
        return item.subMenu.some(sub => !sub.perms || sub.perms.length === 0 || hasPerm(sub.perms));
      }
      return true;
    });

    if (visibleItems.length === 0) return null;

    return (
      <div className="mt-6">
        <p className="font-semibold text-sm mb-2">{title}</p>
        <div className="space-y-1">
          {items.map((item, index) => renderMenuItem(item, title + index))}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* ✅ Mobile Top Navbar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white shadow flex items-center justify-between px-4 py-2">
        <img src={logo} alt="Logo" className="h-8" />
        <div className="flex items-center gap-4 text-gray-700 text-lg">
          <FaEnvelope className="cursor-pointer hover:text-rose-600" />
          <FaBell className="cursor-pointer hover:text-rose-600" />
          <button onClick={() => setMobileMenuOpen(true)}>
            <FaBars />
          </button>
        </div>
      </div>

      {/* ✅ Overlay for mobile */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* ✅ Sidebar (desktop or mobile slide-in) */}
      <div
        className={`fixed top-0 left-0 md:h-full h-[95%] overflow-scroll w-72 bg-white shadow-lg z-50 transition-transform ${
          isOpen || mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } md:relative md:translate-x-0`}
      >
        {/* Mobile Close Button */}
        <div className="md:hidden flex justify-end p-2">
          <button onClick={() => setMobileMenuOpen(false)} className="text-gray-700">
            <FaTimes size={20} />
          </button>
        </div>

        {/* Sidebar Content */}
        <div className="p-4 border-b">
          {(() => {
             const rawImages = getUserdata("restaurant_images");
             let imgUrl = null;
             const envBaseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost/stellaris_Original/stellaris_php-main/";
             const baseUrl = envBaseUrl.endsWith('/') ? envBaseUrl : `${envBaseUrl}/`;
             
             if (rawImages) {
                try {
                   const parsed = typeof rawImages === 'string' ? JSON.parse(rawImages) : rawImages;
                   if (Array.isArray(parsed) && parsed.length > 0) {
                      imgUrl = `${baseUrl}images/restaurant/shop_images/${parsed[0]}`;
                   } else if (typeof parsed === 'string') {
                      imgUrl = `${baseUrl}images/restaurant/shop_images/${parsed}`;
                   }
                } catch(e) {
                   if (typeof rawImages === 'string' && rawImages.length > 0) {
                      imgUrl = `${baseUrl}images/restaurant/shop_images/${rawImages}`;
                   }
                }
             }
             if (imgUrl) {
                return <img src={imgUrl} alt="Business Logo" className="h-16 w-16 object-cover mb-2 rounded-full border shadow-sm" />;
             }
             return <LogoIcon />;
          })()}
          <h1 className="text-lg font-bold mt-3">{getUserdata("restaurantName") || "Stellaris"}</h1>
          <p className="text-sm text-gray-500">Serve More. Stress Less.</p>
        </div>

        <div className="p-4 space-y-2">
          {/* Employee Profile Section */}
          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
            {getUserdata("profile_image") ? (
              <img 
                src={`${process.env.REACT_APP_API_BASE_URL ? (process.env.REACT_APP_API_BASE_URL.endsWith('/') ? process.env.REACT_APP_API_BASE_URL : `${process.env.REACT_APP_API_BASE_URL}/`) : "http://localhost/stellaris_Original/stellaris_php-main/"}images/restaurant/staffs/${getUserdata("profile_image")}`} 
                alt="Profile" 
                className="w-10 h-10 rounded-full object-cover border border-gray-300"
              />
            ) : (
              <FaUser className="text-gray-600 text-2xl" />
            )}
            <div>
              <p className="text-sm font-bold">
                {getUserdata("firstName")} {getUserdata("lastName")}
              </p>
              <p className="text-xs text-gray-500">{getUserdata("roleName") || "Staff Member"}</p>
            </div>
          </div>

          {/* Quick Actions / Attendance (Basic Staff Access) */}
          <div className="mt-6">
            <p className="font-semibold text-sm mb-2">Basic Access</p>
            <div className="space-y-1">
              {renderMenuItem({ label: "Profile", icon: <FaUserCircle />, path: "/restaurant-staff/employee/profile", perms: [128] }, 'profile')}
              {renderMenuItem({ label: "Dashboard", icon: <FaChartPie />, path: "/restaurant-staff/employee", perms: [1] }, 'dashboard')}
              {renderMenuItem({ label: "Attendance", icon: <FaChartPie />, path: "/restaurant-staff/employee/attendance", perms: [117] }, 'attendance')}
              {renderMenuItem({ label: "Payroll", icon: <FaMoneyBill />, path: "/restaurant-staff/employee/payroll", perms: [118] }, 'payroll')}
            </div>
          </div>

          {/* Business Section */}
          {renderSection("Business", businessItems)}

          {/* Management Section */}
          {renderSection("Management", managementItems)}

          {/* Help Chat */}
          {renderSection("Help", helpItems)}

          {/* Apps Section */}
          {renderSection("Apps", appsItems)}

          {/* Settings Section */}
          <div className="mt-6">
            <p className="font-semibold text-sm mb-2">Settings</p>
            <div className="space-y-1">
              {settingsItems.map((item, index) => renderMenuItem(item, "settings_" + index))}

              {/* Logout - Always accessible */}
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  logoutUser();
                }}
                className="w-full flex items-center gap-2 text-sm p-2 rounded text-red-600 hover:bg-red-50 text-left transition-all"
              >
                <FaSignOutAlt /> <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StaffSideBar;